/**
 * KP id → human-friendly hierarchical display name resolver.
 *
 * 林媽媽 2026-05-21 01:08 round-2 complaint: parent dashboard's 最弱單元/最強單元
 * list was showing raw kpIds like `math.g3s2.frac-add` or even shorter kpId.
 * Parents have no way to know what "math.fraction.compare" actually is — they
 * need natural-language context: subject, chapter, and lesson title.
 *
 * Output format: 「<科目> › 第 N 章 › <KP 標籤>」, for example:
 *   - "數學 › 第 4 章 › 同分母分數大小比較與加減"
 *   - "國語 › 第 3 章 › 段落主旨歸納"
 *
 * Source of truth: CURRICULUM tree in src/learn/curriculum/index.ts. We resolve
 * by walking subject → chapter → KP, mirroring getKPLabel/getChapterForKP but
 * returning the full hierarchical path.
 *
 * Fallback (kpId not in CURRICULUM): split on '.' and capitalise each segment
 * so the parent still sees something readable rather than a raw dotted id.
 * Never echo the raw kpId verbatim — that was the original complaint.
 */

import { CURRICULUM } from '../curriculum';
import type { Subject } from '../../shared/types';

const SUBJECT_LABELS: Record<Subject, string> = {
  math: '數學',
  chinese: '國語',
  english: '英語',
  nature: '自然',
  social: '社會',
};

/** Capitalise the first character of each '.'-separated segment for the fallback. */
function capitaliseFallback(kpId: string): string {
  return kpId
    .split('.')
    .map(seg => (seg.length === 0 ? seg : seg[0].toUpperCase() + seg.slice(1)))
    .join(' › ');
}

/**
 * Resolve a KP id to its human-friendly hierarchical display name.
 *
 * Returns a 「科目 › 第 N 章 › KP 標籤」 string when the kpId is found in
 * CURRICULUM, otherwise falls back to a capitalised '.'-split rendition.
 */
export function friendlyKPName(kpId: string): string {
  for (const subjectCurriculum of CURRICULUM) {
    for (const chapter of subjectCurriculum.chapters) {
      for (const kp of chapter.kps) {
        if (kp.id === kpId) {
          const subjectLabel = SUBJECT_LABELS[subjectCurriculum.subject] ?? subjectCurriculum.label;
          return `${subjectLabel} › 第 ${chapter.number} 章 › ${kp.label}`;
        }
      }
    }
  }
  return capitaliseFallback(kpId);
}
